import { useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { SET_USER } from "redux/user/actions.js";
import { apiRequest } from "util/services";
import { hasPivonySonicPlan } from "util/pivonySonicAccess";
import { PLAN_ACCESS, sonicEngageAccess } from "util/entitlements";

/**
 * Hydrate subscription row and expose package 120 (Pivony Sonic) entitlement. ``canView`` also
 * covers a plan that has ended (``readOnly``): the Engage console then opens read-only.
 */
export function usePivonySonicSubscription(authenticated, user) {
  const dispatch = useDispatch();
  const subscriptionStatus = useSelector((state) => state.userReducer);
  const [fetchFinished, setFetchFinished] = useState(false);

  const hydrated =
    subscriptionStatus?.PackageId != null ||
    subscriptionStatus?.PackageName != null ||
    fetchFinished;

  const canAccess = hasPivonySonicPlan(subscriptionStatus);
  const engageAccess = sonicEngageAccess(subscriptionStatus);
  const readOnly = engageAccess === PLAN_ACCESS.READ_ONLY;

  useEffect(() => {
    setFetchFinished(false);
  }, [user?.uid]);

  useEffect(() => {
    if (authenticated !== "logged") {
      setFetchFinished(true);
      return undefined;
    }

    const hasRow =
      subscriptionStatus?.PackageId != null ||
      subscriptionStatus?.PackageName != null;
    if (hasRow || !user?.uid) {
      setFetchFinished(true);
      return undefined;
    }

    let cancelled = false;
    apiRequest("subscriptions/all", "POST", { user_id: user.uid }, true).then(
      (result) => {
        if (cancelled) return;
        setFetchFinished(true);
        if (!result?.error && result?.[0]) {
          dispatch({ type: SET_USER, payload: result[0] });
        }
      }
    );

    return () => {
      cancelled = true;
    };
  }, [
    authenticated,
    user?.uid,
    subscriptionStatus?.PackageId,
    subscriptionStatus?.PackageName,
    dispatch,
  ]);

  return {
    subscriptionStatus,
    hydrated,
    canAccess,
    canView: engageAccess !== PLAN_ACCESS.NONE,
    readOnly,
    loading: authenticated === "logged" && !hydrated,
  };
}
